import { useCallback, useEffect, useRef, useState } from 'react';
import { resHandle } from '../../../helper';
import { registrationReportList, registrationReportStatusNote } from '../../../services/directorServices';
import { adaptReport, patchReportRow } from '../registrationAdapter';

// Season = programs starting in one calendar year, Jan 1 to Dec 31 in the browser's time zone.
export const seasonRange = year => ({
  from: new Date(year, 0, 1).getTime(),
  to: new Date(year, 11, 31, 23, 59, 59, 999).getTime(),
});

const EMPTY = { camp: null, range: null, programs: [], rows: [] };

const LOAD_ERROR = "Couldn't load registrations. Check your connection and try again.";
const SAVE_ERROR = "Couldn't save the status note. Check your connection and try again.";

/*
 * Loads the report for a camp and season: GET registrationReport/list (campsez_api
 * docs/director-reporting-api.md), one response with every row; the page filters in the browser.
 * The API answers HTTP 200 with statusCode 0 on errors (78 for a camp the director can't see), so
 * its message is shown as the error. Only the latest request's answer is used.
 */
export function useRegistrationReport(campCode, year) {
  const key = campCode ? `${campCode}:${year}` : '';
  const [state, setState] = useState({ loading: true, refreshing: false, error: '', data: EMPTY });
  const requestId = useRef(0);

  // One request for the season. Resolves { ok, data } or { ok: false, error }; never rejects.
  const fetchReport = useCallback(() => {
    const { from, to } = seasonRange(year);
    return registrationReportList({ campCode, programStartFrom: from, programStartTo: to })
      .then(res => {
        const { status, data } = resHandle(res);
        // responseData is the report itself: { camp, range, programs, questions, rows }.
        if (status && data?.rows) return { ok: true, data: adaptReport(data) };
        return { ok: false, error: (!status && typeof data === 'string' && data) || LOAD_ERROR };
      })
      .catch(err => {
        // Network failure, or a row the adapter couldn't read.
        console.error('Registrations report:', err);
        return { ok: false, error: LOAD_ERROR };
      });
  }, [campCode, year]);

  // First load, camp / season change and Try again: the loader replaces the table.
  const load = useCallback(() => {
    if (!campCode) return;
    const id = ++requestId.current;
    setState(prev => ({ ...prev, loading: true, refreshing: false, error: '' }));
    fetchReport().then(result => {
      if (id !== requestId.current) return;
      setState({ loading: false, refreshing: false, error: result.ok ? '' : result.error, data: result.ok ? result.data : EMPTY });
    });
  }, [campCode, fetchReport]);

  /*
   * Refresh button: the current rows stay on screen until the new ones arrive, and stay if the
   * request fails. Resolves { ok }, { ok: false, error } (the page shows the message) or
 * { ok: false, stale: true } when a newer load replaced it.
   */
  const refresh = useCallback(() => {
    if (!campCode) return Promise.resolve({ ok: false, error: LOAD_ERROR });
    const id = ++requestId.current;
    setState(prev => ({ ...prev, refreshing: true }));
    return fetchReport().then(result => {
      // Overtaken by a camp / season change or another load: nothing to report.
      if (id !== requestId.current) return { ok: false, stale: true };
      setState(prev => (result.ok ? { loading: false, refreshing: false, error: '', data: result.data } : { ...prev, refreshing: false }));
      return result.ok ? { ok: true } : { ok: false, error: result.error };
    });
  }, [campCode, fetchReport]);

  useEffect(() => {
    load();
    // A camp or season change, or leaving the page, makes any answer still in flight stale.
    return () => {
      requestId.current += 1;
    };
  }, [load]);

  /*
   * Saves a row's status note (POST registrationReport/statusNote; "" clears it) and, once the API
   * has saved it, shows the saved text on the row: the API trims it. Resolves { ok, statusNote } or
   * { ok: false, error } with the API's message; the row is unchanged on failure.
   */
  const saveStatusNote = useCallback(
    (row, statusNote) =>
      registrationReportStatusNote({ campCode, bookingId: row.bookingId, programId: row.programId, camperId: row.camperId, statusNote })
        .then(res => {
          const { status, data } = resHandle(res);
          if (!status) return { ok: false, error: (typeof data === 'string' && data) || SAVE_ERROR };
          const saved = typeof data?.statusNote === 'string' ? data.statusNote : statusNote.trim();
          setState(prev => ({ ...prev, data: patchReportRow(prev.data, row.id, { statusNote: saved }) }));
          return { ok: true, statusNote: saved };
        })
        .catch(() => ({ ok: false, error: SAVE_ERROR })),
    [campCode]
  );

  return { ...state, dataKey: key, reload: load, refresh, saveStatusNote };
}
